import {useRef, useCallback, useLayoutEffect, useEffect, KeyboardEventHandler} from "react";
import SearchOverlay, {getDefaultSavedSearchFilter} from "./SearchOverlay";
import {useSearchInput} from "./useSearchInput";
import {searchActions, useSearchBehaviour, useSearchStore} from "./useSearch";
import ActiveSearchFilters from "./ActiveSearchFilters";
import {cx, RawSpinner, TooltipForChild, useEsc, useGlobalKeyPress} from "@cdx/common";
import useSelectedProjects from "../../lib/hooks/useSelectedProjects";
import XTextButton from "../../components/xui/XTextButton";
import {searchStyles as styles} from "./search.css";
import {useLocation, useHistory} from "react-router";
import {useIsSelectingCards} from "../card-selection/useSelectedCards";
import {Box, Col, DSButton, DSIconArchive, DSIconSearch, DSShortcutKey, Row} from "@cdx/ds";
import {colorThemes} from "@cdx/ds/css/themes/color-overwrites.css";
import dsStyles from "@cdx/ds/css/index.css";
import {SearchFilter} from "./search-types";
import {Root} from "../../cdx-models/Root";
import {containsFilter, filtersAreEqual, removeFilters} from "../../lib/search-utils";
import {FEATURE_FLAGS, hasFeatureFlag} from "../../components/useFeatureFlag";

const useMyCardsShortcut = ({root, searchFilters}: {root: Root; searchFilters: SearchFilter[]}) => {
  const meId = root.loggedInUser?.$meta.get("id", null);
  useGlobalKeyPress({
    key: "q",
    disabled: !meId,
    fn: () => {
      if (!meId) return;
      const meFilter: SearchFilter[] = [{category: "cardOwner", value: meId}];
      searchActions.setFilters(filtersAreEqual(searchFilters, meFilter) ? [] : meFilter);
    },
    description: 'Add/dismiss "my cards" filter',
    category: "Search",
  });
};

type InnerSearchProps = {
  onLight?: boolean;
  getCardCount: () => number;
  searchWithinLabel: string;
  root: Root;
  hasExternalArchiveButton?: boolean;
  hasExternalMyCardsButton?: boolean;
  basedOnFullLibrary?: boolean;
  notAffectedByProjectSelection?: boolean;
  defaultArchiveStorageKey: string | null;
  searchArenaName: string;
  growOnActive?: boolean;
};

const InnerSearch = ({
  onLight,
  getCardCount,
  searchWithinLabel,
  root,
  hasExternalArchiveButton,
  hasExternalMyCardsButton: rawMyCardsButton,
  basedOnFullLibrary,
  notAffectedByProjectSelection,
  defaultArchiveStorageKey,
  searchArenaName,
  growOnActive,
}: InnerSearchProps) => {
  const inputRef = useRef<HTMLInputElement>(null);
  const searchState = useSearchStore();
  const {filters, includeArchivedCards, isFocussed, forceOr} = searchState;
  const searchStateRef = useRef(searchState);
  searchStateRef.current = searchState;
  const onClose = useCallback(() => {
    if (isFocussed && inputRef.current) inputRef.current.blur();
    searchActions.close();
  }, [isFocussed]);

  const history = useHistory();
  const location = useLocation<any>();
  const hasExternalMyCardsButton = hasFeatureFlag(root.account, FEATURE_FLAGS.myCardsButton)
    ? rawMyCardsButton
    : false;

  const isSelecting = useIsSelectingCards();

  useEffect(() => {
    if (isSelecting && isFocussed) {
      if (inputRef.current) inputRef.current.blur();
      searchActions.close();
    }
  }, [isFocussed, isSelecting]);

  useLayoutEffect(() => {
    if (searchStateRef.current.isFocussed && inputRef.current) inputRef.current.focus();
  }, []);

  const projects = useSelectedProjects(root);
  const [
    {searchValue, hasPendingSuggestions, selectedIndex, suggestions, waitingForLoadedTimeoutId},
    searchInputActions,
  ] = useSearchInput({history, closeSearch: onClose, projects});

  const containerRef = useRef<HTMLDivElement>(null);
  useMyCardsShortcut({root, searchFilters: filters});
  useSearchBehaviour({location, defaultArchiveStorageKey, searchArenaName, history});

  const showIncludeArchivedCards = !hasExternalArchiveButton && includeArchivedCards;

  const onSlash = useCallback(() => {
    inputRef.current?.focus();
  }, []);
  useGlobalKeyPress({key: "/", fn: onSlash, description: "Open search", category: null});

  const getShownFilters = () => {
    if (!hasExternalMyCardsButton || !root.loggedInUser) return filters;
    const myCardsFilter = getDefaultSavedSearchFilter(root.loggedInUser);
    return filters.filter((f) => !filtersAreEqual([f], myCardsFilter));
  };

  const shownFilters = getShownFilters();
  const isActive = isFocussed || shownFilters.length > 0 || showIncludeArchivedCards;

  const handleKeyDown: KeyboardEventHandler<HTMLInputElement> = (e) => {
    if (e.which === 38) {
      // UP
      searchInputActions.selectAbove();
      e.preventDefault();
    } else if (e.which === 40) {
      // DOWN
      searchInputActions.selectBelow();
      e.preventDefault();
    } else if (e.which === 13) {
      // RETURN
      searchInputActions.selectIndexed();
      e.preventDefault();
    } else if (e.which === 8 && !searchValue) {
      // BACKSPACE
      if (shownFilters.length > 0) {
        searchActions.removeLast();
        e.preventDefault();
      } else if (forceOr) {
        searchActions.toggleForceOr();
      } else if (showIncludeArchivedCards) {
        searchActions.toggleArchived();
        e.preventDefault();
      }
    }
  };

  useEsc(() => {
    if (shownFilters.length > 0) {
      searchActions.removeLast();
    } else {
      return false;
    }
  });

  return (
    <Col
      relative
      minWidth="0"
      ref={containerRef}
      className={cx(
        styles.outerContainer.base,
        growOnActive && !isFocussed && styles.outerContainer.smallInactive
      )}
    >
      <Row
        className={cx(
          styles.container.default,
          onLight ? styles.container.light : styles.container.dark,
          isFocussed && (onLight ? styles.container.lightFocussed : styles.container.darkFocussed)
        )}
        px="12px"
        sp="12px"
        align="center"
        flex="auto"
      >
        {(shownFilters.length > 0 || showIncludeArchivedCards) && (
          <ActiveSearchFilters
            filters={shownFilters}
            root={root}
            includeArchivedCards={showIncludeArchivedCards}
            forceOr={forceOr}
            onRemoveToken={() => inputRef.current?.focus()}
          />
        )}
        <Row as="label" align="center" sp="12px" flex="auto">
          {!isActive && (
            <DSIconSearch
              size={20}
              className={cx(colorThemes.purpleTextOnDark, dsStyles.color.primary)}
            />
          )}
          <input
            type="text"
            value={searchValue}
            onChange={searchInputActions.onChangeSearchInput}
            onKeyDown={handleKeyDown}
            className={cx(styles.input.default, isFocussed && styles.input.focussed)}
            placeholder={shownFilters.length === 0 ? "Search cards..." : "Add filter..."}
            onFocus={() => searchActions.open()}
            ref={inputRef}
          />
          {!isFocussed && (
            <div
              className={cx(
                styles.slashPill.default,
                onLight ? styles.slashPill.light : styles.slashPill.dark
              )}
            >
              /
            </div>
          )}
        </Row>
        {isFocussed && (
          <Row
            className={styles.spinnerBox}
            pr="8px"
            align="center"
            opacity={hasPendingSuggestions ? 1 : 0}
          >
            <RawSpinner size={15} />
          </Row>
        )}
      </Row>
      {isFocussed && (
        <SearchOverlay
          selectedIndex={selectedIndex}
          suggestions={suggestions}
          cardCount={getCardCount()}
          waitingForMore={waitingForLoadedTimeoutId !== null}
          root={root}
          searchInputActions={searchInputActions}
          searchState={searchState}
          searchWithinLabel={searchWithinLabel}
          notAffectedByProjectSelection={notAffectedByProjectSelection}
          hasExternalArchiveButton={hasExternalArchiveButton}
          hasExternalMyCardsButton={hasExternalMyCardsButton}
          basedOnFullLibrary={basedOnFullLibrary}
          onClose={onClose}
          shownFilters={shownFilters}
        />
      )}
    </Col>
  );
};

export const ArchiveButton = ({hasArchivedCards}: {hasArchivedCards?: boolean}) => {
  const includeArchivedCards = useSearchStore((s) => s.includeArchivedCards);
  const handleClick = () => {
    searchActions.toggleArchived();
  };

  return (
    <XTextButton
      square
      active={includeArchivedCards}
      onClick={handleClick}
      color={hasArchivedCards ? "default" : "dim"}
      tooltip="Include archived cards"
    >
      <DSIconArchive size={20} />
    </XTextButton>
  );
};

type SearchProps = InnerSearchProps & {
  preFilters?: JSX.Element;
};

const Search = ({preFilters, ...rest}: SearchProps) => (
  <Row
    sp="12px"
    align="center"
    data-cdx-context="Search"
    flex="auto"
    justify="end"
    style={{flexShrink: 5}}
  >
    {preFilters}
    <InnerSearch {...rest} />
  </Row>
);

export const ShowMyCardsButton = ({root}: {root: Root}) => {
  const myCardsFilter = root.loggedInUser
    ? (getDefaultSavedSearchFilter(root.loggedInUser) as any[])
    : [];
  const searchState = useSearchStore();
  if (!hasFeatureFlag(root.account, FEATURE_FLAGS.myCardsButton)) return null;
  if (process.env.REACT_APP_MODE === "open") return null;

  const active = containsFilter(myCardsFilter, searchState.filters);
  const handleClick = () => {
    if (active) {
      searchActions.setFilters(removeFilters(myCardsFilter, searchState.filters));
    } else {
      searchActions.setFilters(myCardsFilter);
    }
  };
  return (
    <TooltipForChild
      tooltip={
        <Box pa="8px">
          Show all Cards I own <DSShortcutKey>Q</DSShortcutKey>
        </Box>
      }
    >
      <DSButton variant="secondary" size="sm" active={active} onClick={handleClick}>
        My Cards
      </DSButton>
    </TooltipForChild>
  );
};

export default Search;
